import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Form, Input, Button, Space, Alert, Select, Card, Typography } from "antd";
import { LockOutlined, ArrowLeftOutlined } from "@ant-design/icons";

const { Title } = Typography;

function ResetPasswordScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [form] = Form.useForm();
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = t("resetPassword.pageTitle");
  }, [t]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const encodedToken = searchParams.get("token");
    const email = searchParams.get("email");

    if (encodedToken) {
      const decodedToken = encodedToken.replace(/ /g, "+");
      setResetToken(decodedToken);
    }

    if (email) {
      setEmail(email);
    }
  }, [location]);

  const handleResetPassword = async (values) => {
    if (!password || password.length < 6) {
      setResetMessage(t("resetPassword.passwordLengthError"));
      return;
    }

    try {
      setLoading(true);

      const resetRequest = {
        email: email,
        password: password,
        token: resetToken,
      };

      await axios.post("/dotnet_api/v1/auth/reset_password", resetRequest);

      setResetMessage(t("resetPassword.resetSuccessful"));
      setTimeout(() => {
        navigate("/userlogin");
      }, 2000);
    } catch (error) {
      setResetMessage(t("resetPassword.resetError"));
      console.error("Reset password error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (value) => {
    i18n.changeLanguage(value);
  };

  const languageOptions = [
    { value: "en", label: "🇬🇧 English" },
    { value: "fr", label: "🇫🇷 Français" },
  ];

  const handleGoBack = () => {
    navigate("/userlogin");
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 32 }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
            {t("resetPassword.title")}
          </Title>
          {resetMessage && (
            <Alert
              message={resetMessage}
              type={resetMessage.includes("successful") ? "success" : "error"}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Form form={form} name="resetPassword" onFinish={handleResetPassword} layout="vertical">
            <Form.Item
              name="password"
              rules={[{ required: true, message: t("resetPassword.passwordRequired") }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t("resetPassword.newPasswordPlaceholder")}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ width: "100%" }} loading={loading} size="large">
                {loading ? t("resetPassword.resetting") : t("resetPassword.resetButton")}
              </Button>
            </Form.Item>
          </Form>
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={handleGoBack} style={{ marginTop: 8 }}>
            {t("resetPasswordSent.goBack")}
          </Button>
          <Select
            defaultValue={i18n.language}
            style={{ width: 120, marginTop: 16 }}
            onChange={handleLanguageChange}
            options={languageOptions}
          />
        </Card>
      </Space>
  );
}

export default ResetPasswordScreen;